import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckCircleIcon, AlertCircleIcon, ShieldCheck } from "lucide-react";

interface ApprovalActionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: "approve" | "decline" | null;
  onSubmit: (text: string, withdrawConsent?: boolean) => void;
  isSubmitting: boolean;
  signerName?: string;
  savedSignatureImage?: string | null;
}

const ApprovalActionModal: React.FC<ApprovalActionModalProps> = ({
  open,
  onOpenChange,
  action,
  onSubmit,
  isSubmitting,
}) => {
  const { t } = useTranslation(['documents', 'common']);
  const [text, setText] = React.useState("");
  const [consentChecked, setConsentChecked] = React.useState(false);
  const [withdrawConsent, setWithdrawConsent] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setText("");
      setConsentChecked(false);
      setWithdrawConsent(false);
    }
  }, [open]);

  const handleSubmit = () => {
    onSubmit(
      text,
      action === "decline" ? withdrawConsent : undefined,
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      data-pol-id="ntxg89"
      data-pol-file-name="approval-action-modal"
      data-pol-file-type="component"
    >
      <DialogContent
        className={action === "approve" ? "sm:max-w-[500px]" : "sm:max-w-[425px]"}
        data-pol-id="xgl7hy"
        data-pol-file-name="approval-action-modal"
        data-pol-file-type="component"
      >
        <DialogHeader
          data-pol-id="c8nj4q"
          data-pol-file-name="approval-action-modal"
          data-pol-file-type="component"
        >
          <DialogTitle
            className="flex items-center gap-2"
            data-pol-id="c9n7r0"
            data-pol-file-name="approval-action-modal"
            data-pol-file-type="component"
          >
            {action === "approve" ? (
              <>
                <CheckCircleIcon
                  className="h-5 w-5 text-green-500"
                  data-pol-id="sunyen"
                  data-pol-file-name="approval-action-modal"
                  data-pol-file-type="component"
                />
                {t('documents:viewer_approve_document_title')}
              </>
            ) : (
              <>
                <AlertCircleIcon
                  className="h-5 w-5 text-red-500"
                  data-pol-id="xbx41s"
                  data-pol-file-name="approval-action-modal"
                  data-pol-file-type="component"
                />
                {t('documents:viewer_decline_document_title')}
              </>
            )}
          </DialogTitle>
          <DialogDescription
            data-pol-id="p7jmp7"
            data-pol-file-name="approval-action-modal"
            data-pol-file-type="component"
          >
            {action === "approve"
              ? t('documents:viewer_approve_description')
              : t('documents:viewer_decline_description')}
          </DialogDescription>
        </DialogHeader>

        <Textarea
          placeholder={
            action === "approve"
              ? t('documents:viewer_remarks_placeholder')
              : t('documents:viewer_decline_reason_placeholder')
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="resize-none h-24"
          required={action === "decline"}
          data-pol-id="r0ftd6"
          data-pol-file-name="approval-action-modal"
          data-pol-file-type="component"
        />

        {action === "approve" && (
          <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md">
            <div className="flex items-start gap-2 mb-2">
              <ShieldCheck className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-700 dark:text-blue-300">
                {t('documents:esign_disclosure')}
              </p>
            </div>
            <div className="flex items-start gap-2 mt-3">
              <Checkbox
                id="modal-esign-consent"
                checked={consentChecked}
                onCheckedChange={(checked: boolean | "indeterminate") => setConsentChecked(checked === true)}
              />
              <Label htmlFor="modal-esign-consent" className="text-xs font-medium leading-tight cursor-pointer">
                {t('documents:esign_consent_label')}
              </Label>
            </div>
          </div>
        )}

        {action === "decline" && (
          <div className="flex items-start gap-2">
            <Checkbox
              id="modal-withdraw-consent"
              checked={withdrawConsent}
              onCheckedChange={(checked: boolean | "indeterminate") => setWithdrawConsent(checked === true)}
            />
            <Label htmlFor="modal-withdraw-consent" className="text-xs font-medium leading-tight cursor-pointer">
              {t('documents:esign_withdraw_consent')}
            </Label>
          </div>
        )}

        <DialogFooter
          data-pol-id="elxn6t"
          data-pol-file-name="approval-action-modal"
          data-pol-file-type="component"
        >
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            data-pol-id="mpaqh1"
            data-pol-file-name="approval-action-modal"
            data-pol-file-type="component"
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={(action === "approve" && !consentChecked) || (action === "decline" && !text.trim()) || isSubmitting}
            className={action === "approve" ? "bg-green-600 hover:bg-green-700 text-white" : action === "decline" ? "bg-red-600 hover:bg-red-700 text-white" : ""}
            data-pol-id="jhfkka"
            data-pol-file-name="approval-action-modal"
            data-pol-file-type="component"
          >
            {isSubmitting
              ? t('documents:viewer_processing')
              : action === "approve"
                ? t('documents:viewer_sign_and_approve')
                : t('documents:viewer_decline_document')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApprovalActionModal;
